import apiService from './api';

/** Account details returned when logged in as a business-member alias */
export interface AliasAccountDetails {
  aliasId: string;
  aliasUsername: string;
  displayName?: string;
  jointAccountId: string;
  jointAccountName: string;
  role: string;
  canViewBalance: boolean;
  canViewHistory: boolean;
  canSpend: boolean;
  canDeposit: boolean;
  canWithdraw: boolean;
  canInviteMembers: boolean;
  canEditSettings: boolean;
  allowedTabs: string[];
  isJointContext: true;
  created: string;
}

export interface BusinessAccountInfo {
  credentialId: string;
  businessUserId: string;
  businessName: string;
  dbaName?: string;
  fein?: string;
  websiteUrl?: string;
  contactName?: string;
  phoneNumber?: string;
  email?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  isPhoneVerified?: boolean;
  isAddressVerified?: boolean;
  isEmailVerified?: boolean;
}

export interface CompositeAccountDetails {
  personalAccount: UserAccountDetails;
  businessAccount?: BusinessAccountInfo | null;
  aliasContext?: AliasAccountDetails | null;
  showBusinessSection: boolean;
  canEditBusinessInfo: boolean;
  hasVerifiedMember: boolean;
}

export function isCompositeAccountDetails(
  details: UserAccountDetails | AliasAccountDetails | CompositeAccountDetails
): details is CompositeAccountDetails {
  return 'personalAccount' in details;
}

export interface UserAccountDetails {
  userId: string;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  
  // Address
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
  country?: string;
  
  // Personal Info
  dateOfBirth?: string;
  timezone?: string;
  
  // Account Status
  verificationLevel: number;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  isAddressVerified: boolean;
  isBusinessAccount?: boolean;
  businessName?: string;
  dbaName?: string;
  fein?: string;
  websiteUrl?: string;
  
  createDate: string;
  lastModifiedDate?: string;
}

export interface UpdateUserAccountRequest {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  
  /** Email address. Changing email resets verification - user must verify new address. */
  email?: string;
  
  // Address
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  
  // Personal Info
  dateOfBirth?: string;
  timezone?: string;
}

export interface EmailChangeResponse {
  success: boolean;
  message: string;
  codeSent?: boolean;
}

export interface TwoFactorStatusResponse {
  success: boolean;
  isEnabled: boolean;
  method: string;
}

export interface TwoFactorSetupResponse {
  success: boolean;
  qrCodeDataUrl: string;
  manualEntryKey: string;
  backupCodes: string[];
  message?: string;
}

export interface TwoFactorVerifyResponse {
  success: boolean;
  message: string;
}

export interface ReferralLinksResponse {
  usernameReferralCode: string;
  usernameReferralUrl: string;
  isReferralPartner: boolean;
  isMultiReferralPartner: boolean;
  partnerCodes: Array<{
    partnerCodeId: string;
    referralCode: string;
    maskedCode: string;
    shortCode: string;
    shortReferralUrl: string;
    referralUrl: string;
    maskedReferralUrl: string;
    displayName?: string;
    scheduleName: string;
    scheduleCode: string;
    scheduleDescription?: string;
    usageCount: number;
    tier1Count: number;
    tier2Count: number;
    tier3Count: number;
    isActive: boolean;
    expirationDate?: string;
    maxTiers: number;
    tier1Percentage: number;
    tier1MaxPayout?: number;
    tier1DurationMonths?: number;
    tier2Percentage: number;
    tier2MaxPayout?: number;
    tier2DurationMonths?: number;
    tier3Percentage: number;
    tier3MaxPayout?: number;
    tier3DurationMonths?: number;
  }>;
}

export interface ReferralSchedule {
  scheduleId: string;
  scheduleName: string;
  scheduleCode: string;
  description?: string;
  partnerType: string;
  maxTiers: number;
  tier1Percentage: number;
  tier1MaxPayout?: number;
  tier1DurationMonths?: number;
  tier2Percentage: number;
  tier2MaxPayout?: number;
  tier2DurationMonths?: number;
  tier3Percentage: number;
  tier3MaxPayout?: number;
  tier3DurationMonths?: number;
  assignedDate: string;
  isTermsLocked: boolean;
}

export interface ReferredUser {
  referredUserID: string;
  referredUsername: string;
  referredFullName?: string;
  accountCreatedDate: string;
  tierLevel: number;
  cumulativePayout: number;
  expiryDate?: string;
  maxPayoutReached: boolean;
}

export interface CredentialInfo {
  credentialId: string;
  credentialType: string;
  attachedUsername: string;
  isBusinessAccount: boolean;
  status: number;
  createdDate: string;
  businessName?: string;
  fein?: string;
  websiteUrl?: string;
}

export interface UpdateCredentialProfileRequest {
  businessName?: string;
  fein?: string;
  websiteUrl?: string;
  contactName?: string;
  phoneNumber?: string;
  email?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

export interface CredentialsResponse {
  credentials: CredentialInfo[];
  availableUsernames: string[];
}

export interface TransferCredentialResponse {
  success: boolean;
  message: string;
}

const userAccountService = {
  /**
   * Get current user's account details (composite personal + optional business sections).
   */
  async getAccountDetails(): Promise<CompositeAccountDetails> {
    return apiService.get<CompositeAccountDetails>('/api/Account/details');
  },

  /**
   * Update the display name for a business-member alias account.
   * Only valid when isJointContext is true.
   */
  async updateAliasDisplayName(displayName: string): Promise<{ success: boolean }> {
    return apiService.put<{ success: boolean }>('/api/Account/alias-display-name', { displayName });
  },

  /**
   * Update current user's account details
   */
  async updateAccountDetails(request: UpdateUserAccountRequest): Promise<{ message: string }> {
    return apiService.put<{ message: string }>('/api/Account/details', request);
  },

  /**
   * Request an email address change. Sends verification code to the new email.
   */
  async requestEmailChange(newEmail: string): Promise<EmailChangeResponse> {
    return apiService.post<EmailChangeResponse>('/api/Account/request-email-change', { newEmail });
  },

  /**
   * Confirm an email address change with the verification code.
   */
  async confirmEmailChange(newEmail: string, verificationCode: string): Promise<EmailChangeResponse> {
    return apiService.post<EmailChangeResponse>('/api/Account/confirm-email-change', { newEmail, verificationCode });
  },

  /**
   * Get the current 2FA status for the user
   */
  async getTwoFactorStatus(): Promise<TwoFactorStatusResponse> {
    return apiService.get<TwoFactorStatusResponse>('/api/TwoFactor/status');
  },

  /**
   * Initiate 2FA setup - returns QR code and backup codes
   */
  async setupTwoFactor(): Promise<TwoFactorSetupResponse> {
    return apiService.post<TwoFactorSetupResponse>('/api/TwoFactor/setup');
  },

  /**
   * Verify and enable 2FA with the code from authenticator app
   */
  async verifyTwoFactorSetup(code: string): Promise<TwoFactorVerifyResponse> {
    return apiService.post<TwoFactorVerifyResponse>('/api/TwoFactor/verify-setup', { code });
  },

  /**
   * Disable 2FA (requires current TOTP code for verification)
   */
  async disableTwoFactor(code: string): Promise<TwoFactorVerifyResponse> {
    return apiService.post<TwoFactorVerifyResponse>('/api/TwoFactor/disable', { code });
  },

  /**
   * Get current user's referral links
   */
  async getReferralLinks(): Promise<ReferralLinksResponse> {
    return apiService.get<ReferralLinksResponse>('/api/Account/referral-codes');
  },

  /**
   * Get current user's assigned referral schedule
   */
  async getReferralSchedule(): Promise<ReferralSchedule | null> {
    try {
      return await apiService.get<ReferralSchedule>('/api/Account/referral-schedule');
    } catch {
      return null;
    }
  },

  /**
   * Get list of users referred by the current user, optionally scoped to one partner program.
   * @param tierLevel Optional tier filter (1, 2, or 3). Omit for all tiers.
   * @param partnerCodeId Optional partner code GUID to scope results to one program.
   */
  async getReferrals(tierLevel?: number, partnerCodeId?: string): Promise<ReferredUser[]> {
    const params = new URLSearchParams();
    if (tierLevel) params.append('tierLevel', String(tierLevel));
    if (partnerCodeId) params.append('partnerCodeId', partnerCodeId);
    const query = params.toString();
    const url = query ? `/api/Account/referrals?${query}` : '/api/Account/referrals';
    return apiService.get<ReferredUser[]>(url);
  },

  /**
   * Get current user's credentials and available usernames for transfer
   */
  async getCredentials(): Promise<CredentialsResponse> {
    return apiService.get<CredentialsResponse>('/api/Account/credentials');
  },

  /**
   * Transfer a credential to a different username
   * @param credentialId The credential to transfer
   * @param targetUsername The username to transfer the credential to
   */
  async transferCredential(credentialId: string, targetUsername: string): Promise<TransferCredentialResponse> {
    return apiService.post<TransferCredentialResponse>('/api/Account/transfer-credential', {
      credentialId,
      targetUsername,
    });
  },

  async updateCredentialProfile(credentialId: string, data: UpdateCredentialProfileRequest): Promise<{ success: boolean; message: string }> {
    return apiService.put<{ success: boolean; message: string }>(`/api/Account/credentials/${credentialId}/profile`, data);
  },

  async requestBusinessPhoneVerification(credentialId: string, phoneNumber: string): Promise<{ success: boolean; message: string }> {
    return apiService.post<{ success: boolean; message: string }>(
      `/api/Account/credentials/${credentialId}/request-phone-verification`,
      { phoneNumber },
    );
  },

  async confirmBusinessPhoneVerification(credentialId: string, code: string): Promise<{ success: boolean; message: string }> {
    return apiService.post<{ success: boolean; message: string }>(
      `/api/Account/credentials/${credentialId}/confirm-phone-verification`,
      { code },
    );
  },

  async requestBusinessEmailChange(credentialId: string, newEmail: string): Promise<EmailChangeResponse> {
    return apiService.post<EmailChangeResponse>(
      `/api/Account/credentials/${credentialId}/request-email-change`,
      { newEmail },
    );
  },

  async confirmBusinessEmailChange(
    credentialId: string,
    newEmail: string,
    verificationCode: string,
  ): Promise<EmailChangeResponse> {
    return apiService.post<EmailChangeResponse>(
      `/api/Account/credentials/${credentialId}/confirm-email-change`,
      { newEmail, verificationCode },
    );
  },

  async verifyBusinessAddress(
    credentialId: string,
    address: { street1: string; street2?: string; city: string; state: string; zip: string },
  ): Promise<{
    success: boolean;
    isValid: boolean;
    wasCorrected?: boolean;
    message: string;
    verifiedAddress?: { street1: string; street2?: string; city: string; state: string; zip: string };
  }> {
    return apiService.post(`/api/Account/credentials/${credentialId}/verify-address`, address);
  },
};

export default userAccountService;
